import { Building2 } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';

const CHOSEN_KEY = 'crm.orgChosen';

export function hasChosenOrganisation(userId: string): boolean {
  try {
    return sessionStorage.getItem(CHOSEN_KEY) === userId;
  } catch (err) {
    console.warn('could not read organisation choice; not asking again', err);
    return true;
  }
}
export function markOrganisationChosen(userId: string) {
  try {
    sessionStorage.setItem(CHOSEN_KEY, userId);
  } catch (err) {
    console.warn('could not remember organisation choice', err);
  }
}

/**
 * Shown once per browser session to a user with several memberships, so the first thing
 * they see is the organisation they meant, not the default one. Choosing the current one
 * is a no-op; any choice is remembered for the session (the topbar switcher is always there).
 */
export function OrgPicker() {
  const session = useSession();
  const { user, organisation, memberships } = session.me;
  const [open, setOpen] = useState(() => memberships.length > 1 && !hasChosenOrganisation(user.id));
  const [error, setError] = useState<string | null>(null);
  if (!open) return null;

  const choose = async (id: string) => {
    try {
      if (id !== organisation.id) await session.switchOrganisation(id);
      markOrganisationChosen(user.id);
      setOpen(false);
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Choose an organisation</DialogTitle>
          <DialogDescription>
            You belong to several. Which one are you working in today?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {memberships.map((m) => (
            <Button
              key={m.id}
              variant={m.id === organisation.id ? 'default' : 'outline'}
              className="h-auto justify-start gap-3 py-3"
              onClick={() => void choose(m.id)}
              disabled={session.switching}
            >
              <Building2 />
              <span className="flex flex-col items-start">
                <span>{m.name}</span>
                <span className="text-xs opacity-80">{m.role}</span>
              </span>
            </Button>
          ))}
        </div>
        {error && (
          <p className="text-danger text-sm" role="alert">
            {error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
